import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Badge from '@mui/material/Badge';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getLabelMessage } from '@gef-ui/localization/messages';
import ArrowDownToBracket from '@gef-ui/material-icons/ArrowDownToBracket';
import ArrowRight from '@gef-ui/material-icons/ArrowRight';
import ArrowRotateLeft from '@gef-ui/material-icons/ArrowRotateLeft';
import ClockFive from '@gef-ui/material-icons/ClockFive';
import Fire from '@gef-ui/material-icons/Fire';

import { gtmDashboardSectionNames, gtmFilterNames } from 'features/views/dashboard/constants';
import { useButtonClickDataLayer, useDetailButtonClickDataLayer } from 'features/views/dashboard/hooks';
import { toggleButtonGroupValues } from 'features/views/dashboard/marketPlace/constants';
import { getToggleValue } from 'features/views/dashboard/marketPlace/selectors';
import { takeLead } from 'features/views/marketPlace/actions';
import { generateLeadIcon, generateRemainingHoursColor } from 'features/views/marketPlace/utils';
import { getRoutePath } from 'utils';
import { phoneToLink } from 'utils/links';
import { generateFullNameOrBusinessName, joinString } from 'utils/string';

import RemainingTimeDoughnutChart from './RemainingTimeDoughnutChart';

export const LeadNew = ({ lead, availableInMarketPlace = true, showDoughnutChart = true }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const pushToClickButtonDataLayer = useButtonClickDataLayer();
	const pushToClickDetailButtonDataLayer = useDetailButtonClickDataLayer();
	const dashboardTakenLeadsToggleValue = useSelector(getToggleValue);

	const showSLA = useMemo(
		() =>
			!availableInMarketPlace &&
			dashboardTakenLeadsToggleValue === toggleButtonGroupValues.TAKEN &&
			!isNilOrEmpty(lead?.sla),
		[availableInMarketPlace, dashboardTakenLeadsToggleValue, lead?.sla],
	);

	const leadHasSubtype = lead?.taskSubtypeId !== -1;

	const { client = {}, totalHours, remainingHours } = lead;
	const { address, businessName, surname, firstName, correctPhone, correctEmail } = client;

	const handleTakeLead = useCallback(
		(leadId) => async () => {
			// GTM data layer push only for dashboard
			if (window.location.pathname === '/dashboard') {
				pushToClickButtonDataLayer({
					value: gtmDashboardSectionNames.AVAILABLE_LEADS,
					button_text: 'převzetí',
				})();
			}

			const leadHasBeenSuccesfullyTaken = await dispatch(takeLead(leadId));
			if (leadHasBeenSuccesfullyTaken) {
				const { pathname, state } = getRoutePath('leadDetail', { leadId }, true);
				navigate(pathname, { state });
			}
		},
		[dispatch, navigate, pushToClickButtonDataLayer],
	);

	const handleRedirect = useCallback(
		(leadId) => () => {
			const filterType =
				dashboardTakenLeadsToggleValue === toggleButtonGroupValues.DRAFT
					? gtmFilterNames.DRAFT
					: gtmFilterNames.TAKEN;
			pushToClickDetailButtonDataLayer({
				value: gtmDashboardSectionNames.TAKEN_LEADS,
				filter_type: filterType,
			})();

			const { pathname, state } = getRoutePath('leadDetail', { leadId }, true);
			navigate(pathname, { state });
		},
		[navigate, pushToClickDetailButtonDataLayer, dashboardTakenLeadsToggleValue],
	);

	const generateContentBasedOnType = useCallback(() => {
		if (availableInMarketPlace) {
			// normal marketPlace
			return (
				<>
					<Typography variant="body1">
						{generateFullNameOrBusinessName(businessName, surname, firstName)}
					</Typography>
					<Typography variant="body1" color="grey.600">
						{joinString(' ', [address?.zipCode, address?.municipality])}
					</Typography>
				</>
			);
		} else {
			// dashboard takenOrInProgress lead
			return (
				<>
					{!isNilOrEmpty(correctPhone) && phoneToLink(correctPhone, true)}
					{!isNilOrEmpty(correctEmail) && (
						<Typography variant="body1">
							<Link sx={{ wordBreak: 'break-all' }} underline="none" href={`mailto:${correctEmail}`}>
								{correctEmail}
							</Link>
						</Typography>
					)}
					<Typography variant="body1" color="grey.600">
						{joinString(' ', [address?.zipCode, address?.municipality])}
					</Typography>
				</>
			);
		}
	}, [
		address?.municipality,
		address?.zipCode,
		correctEmail,
		correctPhone,
		businessName,
		firstName,
		surname,
		availableInMarketPlace,
	]);

	return (
		<Badge
			anchorOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
			badgeContent={<Fire sx={{ fontSize: '20px' }} />}
			color="primary"
			invisible={availableInMarketPlace || !lead?.isFromMarket}
			sx={{
				width: '100%',
				height: '100%',
				'& .MuiBadge-anchorOriginTopRight': {
					right: '5px',
					padding: 0,
				},
			}}
		>
			<Badge
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				badgeContent={<ArrowRotateLeft sx={{ fontSize: '20px' }} />}
				color="primary"
				invisible={!lead?.returned}
				sx={{
					width: '100%',
					height: '100%',
					'& .MuiBadge-anchorOriginTopLeft': {
						left: '5px',
						padding: 0,
					},
				}}
			>
				<Card sx={{ width: '100%', boxShadow: '0px 1px 2px 0px #0000001F', borderRadius: 1 }}>
					<CardContent
						sx={{
							px: 2,
							pt: 2,
							'&:last-child': {
								paddingBottom: 1.5,
							},
						}}
					>
						<Grid container>
							<Grid size={2}>
								<Stack
									spacing={1}
									sx={{
										alignItems: 'center',
									}}
								>
									{generateLeadIcon({
										taskTypeId: lead?.taskTypeId,
										taskSubtypeId: lead?.taskSubtypeId,
										width: 35,
										height: 35,
									})}
									{showSLA && (
										<Tooltip title={getLabelMessage('marketPlace:slaIconText')}>
											<IconButton
												sx={{
													color: generateRemainingHoursColor({ totalHours, remainingHours }),
													cursor: 'default',
												}}
												disableRipple
											>
												<ClockFive />
											</IconButton>
										</Tooltip>
									)}
								</Stack>
							</Grid>
							<Grid
								sx={{
									pl: 1,
								}}
								size={10}
							>
								<Stack spacing={0.5}>
									<Stack
										direction="row"
										sx={{
											justifyContent: 'space-between',
										}}
									>
										<Stack>
											<Typography variant="h5" color="text.primary">
												{availableInMarketPlace
													? lead?.taskTypeName
													: generateFullNameOrBusinessName(businessName, surname, firstName)}
											</Typography>
											{leadHasSubtype && (
												<Typography variant="subtitle1" color="grey.600">
													{lead?.taskSubtypeName}
												</Typography>
											)}
										</Stack>
										{availableInMarketPlace ? (
											<IconButton
												sx={{ color: 'primary.main' }}
												onClick={handleTakeLead(lead?.id)}
												disableRipple
											>
												<ArrowDownToBracket />
											</IconButton>
										) : (
											<IconButton
												sx={{ color: 'primary.main' }}
												onClick={handleRedirect(lead?.id)}
												disableRipple
											>
												<ArrowRight />
											</IconButton>
										)}
									</Stack>
									<Divider />
									<Stack
										direction="row"
										sx={{
											justifyContent: 'space-between',
											alignItems: 'center',
										}}
									>
										<Stack>{generateContentBasedOnType()}</Stack>
										{showDoughnutChart && (
											<RemainingTimeDoughnutChart
												remainingTimeData={{
													totalHours,
													remainingHours,
												}}
											/>
										)}
									</Stack>
								</Stack>
							</Grid>
						</Grid>
					</CardContent>
				</Card>
			</Badge>
		</Badge>
	);
};

LeadNew.propTypes = {
	availableInMarketPlace: PropTypes.bool,
	lead: PropTypes.object,
	showDoughnutChart: PropTypes.bool,
};

export default LeadNew;
